import React, { useState, useMemo } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, TablePagination, TableSortLabel, Box, Typography, Chip,
  IconButton, Menu, MenuItem, ListItemIcon, ListItemText,
  Collapse, useTheme, Skeleton, Button,
} from '@mui/material';
import {
  Edit, Delete, ContentCopy, DriveFileMove, Palette, ExpandMore,
  ExpandLess, MoreVert, Checklist, Grade, QuestionAnswer, Assignment,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { useTenders } from '../../hooks/TenderContext';
import { useAuth } from '../../hooks/AuthContext';
import { CATEGORIES, ROW_COLORS, ITEMS_PER_PAGE_OPTIONS, DEFAULT_ITEMS_PER_PAGE, TENDER_SUB_PAGES } from '../../utils/constants';
import { STATUS_COLORS, PRIORITY_COLORS, RESULT_COLORS } from '../../theme/theme';
import { formatDate } from '../../utils/helpers';

const SUB_PAGE_ICONS = {
  Checklist: <Checklist fontSize="small" />,
  Grade: <Grade fontSize="small" />,
  QuestionAnswer: <QuestionAnswer fontSize="small" />,
  Assignment: <Assignment fontSize="small" />,
};

const TenderTable = ({ onOpenSubPage }) => {
  const theme = useTheme();
  const {
    filteredTenders, loading, openEditForm, deleteTender,
    moveTender, copyTender, updateRowColor, selectedCategory,
  } = useTenders();
  const { canEdit, canDelete, canMove, hasPermission } = useAuth();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ITEMS_PER_PAGE);
  const [orderBy, setOrderBy] = useState('id');
  const [order, setOrder] = useState('desc');
  const [expandedRow, setExpandedRow] = useState(null);
  const [contextMenu, setContextMenu] = useState(null);
  const [colorMenu, setColorMenu] = useState(null);
  const [selectedTender, setSelectedTender] = useState(null);

  const handleSort = (field) => {
    const isAsc = orderBy === field && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(field);
  };

  const sortedTenders = useMemo(() => {
    return [...filteredTenders].sort((a, b) => {
      let aVal = a[orderBy];
      let bVal = b[orderBy];
      if (aVal === null || aVal === undefined) return 1;
      if (bVal === null || bVal === undefined) return -1;
      if (typeof aVal === 'string') {
        return order === 'asc' ? aVal.localeCompare(bVal, 'he') : bVal.localeCompare(aVal, 'he');
      }
      return order === 'asc' ? aVal - bVal : bVal - aVal;
    });
  }, [filteredTenders, orderBy, order]);

  const paginatedTenders = sortedTenders.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleContextMenu = (event, tender) => {
    event.preventDefault();
    setSelectedTender(tender);
    setContextMenu({ mouseX: event.clientX, mouseY: event.clientY });
  };

  const handleCloseContext = () => setContextMenu(null);

  const handleMove = async (category) => {
    if (selectedTender) await moveTender(selectedTender.rowKey, category);
    handleCloseContext();
  };

  const handleCopy = async (category) => {
    if (selectedTender) await copyTender(selectedTender.rowKey, category);
    handleCloseContext();
  };

  const handleColorSelect = async (color) => {
    if (selectedTender) await updateRowColor(selectedTender.rowKey, color);
    setColorMenu(null);
    setSelectedTender(null);
  };

  const handleDelete = async () => {
    if (selectedTender && window.confirm('האם אתה בטוח שברצונך למחוק מכרז זה?')) {
      await deleteTender(selectedTender.rowKey);
    }
    handleCloseContext();
  };

  const renderChip = (value, colorMap) => {
    if (!value) return '—';
    const colors = colorMap[value] || {};
    return (
      <Chip
        label={value} size="small"
        sx={{
          bgcolor: colors.bg || theme.palette.action.selected,
          color: colors.color || theme.palette.text.primary,
          fontWeight: 500, fontSize: '0.75rem', height: 24,
        }}
      />
    );
  };

  const columns = [
    { field: 'id', label: 'מס"ד', width: 60 },
    { field: 'tender_number', label: 'מספר מכרז', width: 110 },
    { field: 'client', label: 'מזמין', width: 140 },
    { field: 'subject', label: 'נושא המכרז', width: 200, flex: true },
    { field: 'field', label: 'תחום', width: 100 },
    { field: 'status', label: 'סטטוס', width: 90 },
    { field: 'manager', label: 'מנהל', width: 100 },
    { field: 'submission_date', label: 'מועד הגשה', width: 110 },
    { field: 'priority', label: 'תעדוף', width: 80 },
    { field: 'win_status', label: 'תוצאה', width: 110 },
  ];

  const showActions = canEdit || canDelete || canMove;

  if (loading) {
    return (
      <Paper sx={{ p: 2, border: `1px solid ${theme.palette.divider}` }}>
        {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} height={48} sx={{ my: 0.5 }} />)}
      </Paper>
    );
  }

  if (filteredTenders.length === 0) {
    return (
      <Paper sx={{ p: 6, textAlign: 'center', border: `1px solid ${theme.palette.divider}` }}>
        <Typography variant="h6" color="text.secondary" gutterBottom>לא נמצאו מכרזים</Typography>
        <Typography variant="body2" color="text.secondary">נסה לשנות את הסינון או ליצור מכרז חדש</Typography>
      </Paper>
    );
  }

  return (
    <>
      <TableContainer
        component={Paper}
        sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2, overflow: 'auto' }}
      >
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" sx={{ width: 40 }} />
              {columns.map(col => (
                <TableCell
                  key={col.field}
                  sx={{
                    fontWeight: 600, width: col.width,
                    ...(col.flex && { minWidth: col.width }),
                    bgcolor: theme.palette.background.paper,
                    whiteSpace: 'nowrap',
                  }}
                >
                  <TableSortLabel
                    active={orderBy === col.field}
                    direction={orderBy === col.field ? order : 'asc'}
                    onClick={() => handleSort(col.field)}
                  >
                    {col.label}
                  </TableSortLabel>
                </TableCell>
              ))}
              {showActions && (
                <TableCell sx={{ width: 60, bgcolor: theme.palette.background.paper }}>פעולות</TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            <AnimatePresence>
              {paginatedTenders.map((tender, index) => (
                <React.Fragment key={tender.rowKey}>
                  <TableRow
                    component={motion.tr}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: index * 0.02 }}
                    hover
                    onContextMenu={(e) => showActions ? handleContextMenu(e, tender) : null}
                    onClick={() => setExpandedRow(expandedRow === tender.rowKey ? null : tender.rowKey)}
                    sx={{
                      cursor: 'pointer',
                      bgcolor: tender.row_color || 'transparent',
                      transition: 'background-color 0.2s ease',
                      '&:hover': {
                        bgcolor: tender.row_color ? `${tender.row_color}CC` : theme.palette.action.hover,
                      },
                    }}
                  >
                    <TableCell padding="checkbox">
                      <IconButton size="small">
                        {expandedRow === tender.rowKey ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                      </IconButton>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 500 }}>{tender.id}</TableCell>
                    <TableCell>{tender.tender_number || '—'}</TableCell>
                    <TableCell>{tender.client}</TableCell>
                    <TableCell sx={{ maxWidth: 200 }}>
                      <Typography variant="body2" noWrap title={tender.subject}>{tender.subject}</Typography>
                    </TableCell>
                    <TableCell>{tender.field || '—'}</TableCell>
                    <TableCell>{renderChip(tender.status, STATUS_COLORS)}</TableCell>
                    <TableCell>{tender.manager || '—'}</TableCell>
                    <TableCell>{formatDate(tender.submission_date)}</TableCell>
                    <TableCell>{renderChip(tender.priority, PRIORITY_COLORS)}</TableCell>
                    <TableCell>{renderChip(tender.win_status, RESULT_COLORS)}</TableCell>
                    {showActions && (
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedTender(tender);
                            setContextMenu({ mouseX: e.clientX, mouseY: e.clientY });
                          }}
                        >
                          <MoreVert fontSize="small" />
                        </IconButton>
                      </TableCell>
                    )}
                  </TableRow>

                  <TableRow>
                    <TableCell colSpan={showActions ? 12 : 11} sx={{ p: 0, border: 0 }}>
                      <Collapse in={expandedRow === tender.rowKey}>
                        <Box sx={{
                          p: 2.5,
                          bgcolor: 'rgba(0,0,0,0.02)',
                          borderBottom: `1px solid ${theme.palette.divider}`,
                        }}>
                          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5, color: theme.palette.primary.main }}>
                            פרטים נוספים
                          </Typography>
                          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ minWidth: 80 }}>
                                גורם מטפל:
                              </Typography>
                              <Typography variant="body2" fontWeight={500}>{tender.handler || '—'}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ minWidth: 80 }}>
                                אופן זכייה:
                              </Typography>
                              <Typography variant="body2" fontWeight={500}>{tender.win_method || '—'}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ minWidth: 100 }}>
                                מפגש מציעים:
                              </Typography>
                              <Typography variant="body2" fontWeight={500}>{formatDate(tender.bidders_meeting)}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ minWidth: 100 }}>
                                שאלות הבהרה:
                              </Typography>
                              <Typography variant="body2" fontWeight={500}>{formatDate(tender.clarification_date)}</Typography>
                            </Box>
                          </Box>

                          {/* Notes - always visible */}
                          <Box sx={{ mt: 2, p: 1.5, bgcolor: '#FFF8E1', borderRadius: 1, border: '1px solid #FFE082' }}>
                            <Typography variant="caption" color="text.secondary" fontWeight={600}>
                              הערות:
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 0.5 }}>
                              {tender.notes || 'אין הערות'}
                            </Typography>
                          </Box>

                          {/* Decision notes */}
                          {tender.decision_notes && (
                            <Box sx={{ mt: 1, p: 1.5, bgcolor: '#E3F2FD', borderRadius: 1, border: '1px solid #90CAF9' }}>
                              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                החלטה / הערות:
                              </Typography>
                              <Typography variant="body2" sx={{ mt: 0.5 }}>{tender.decision_notes}</Typography>
                            </Box>
                          )}

                          {/* Sub-page buttons */}
                          {onOpenSubPage && (
                            <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                              {TENDER_SUB_PAGES.map(sp => (
                                <Button
                                  key={sp.key}
                                  variant="outlined"
                                  size="small"
                                  startIcon={SUB_PAGE_ICONS[sp.icon]}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onOpenSubPage(tender, sp.key);
                                  }}
                                  sx={{
                                    borderRadius: 2,
                                    fontWeight: 500,
                                    textTransform: 'none',
                                    borderColor: theme.palette.divider,
                                    '&:hover': { borderColor: theme.palette.primary.main, bgcolor: theme.palette.primary.main + '08' },
                                  }}
                                >
                                  {sp.label}
                                </Button>
                              ))}
                            </Box>
                          )}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
            </AnimatePresence>
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={filteredTenders.length}
        page={page}
        onPageChange={(_, p) => setPage(p)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        rowsPerPageOptions={ITEMS_PER_PAGE_OPTIONS}
        labelRowsPerPage="שורות בעמוד:"
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} מתוך ${count}`}
      />

      {/* CONTEXT MENU */}
      <Menu
        open={contextMenu !== null}
        onClose={handleCloseContext}
        anchorReference="anchorPosition"
        anchorPosition={contextMenu ? { top: contextMenu.mouseY, left: contextMenu.mouseX } : undefined}
      >
        {canEdit && (
          <MenuItem onClick={() => { openEditForm(selectedTender); handleCloseContext(); }}>
            <ListItemIcon><Edit fontSize="small" /></ListItemIcon>
            <ListItemText>עריכה</ListItemText>
          </MenuItem>
        )}
        {canDelete && (
          <MenuItem onClick={handleDelete}>
            <ListItemIcon><Delete fontSize="small" color="error" /></ListItemIcon>
            <ListItemText>מחיקה</ListItemText>
          </MenuItem>
        )}
        {hasPermission('tender:color') && (
          <MenuItem onClick={(e) => { handleCloseContext(); setColorMenu(e.currentTarget); }}>
            <ListItemIcon><Palette fontSize="small" /></ListItemIcon>
            <ListItemText>צבע שורה</ListItemText>
          </MenuItem>
        )}
        {canMove && Object.entries(CATEGORIES)
          .filter(([key]) => key !== selectedCategory)
          .map(([key, cat]) => (
            <MenuItem key={key} onClick={() => handleMove(key)}>
              <ListItemIcon><DriveFileMove fontSize="small" sx={{ transform: 'scaleX(-1)' }} /></ListItemIcon>
              <ListItemText>העבר ל{cat.label}</ListItemText>
            </MenuItem>
          ))
        }
        {canMove && Object.entries(CATEGORIES)
          .filter(([key]) => key !== selectedCategory)
          .slice(0, 3)
          .map(([key, cat]) => (
            <MenuItem key={`copy-${key}`} onClick={() => handleCopy(key)}>
              <ListItemIcon><ContentCopy fontSize="small" /></ListItemIcon>
              <ListItemText>העתק ל{cat.label}</ListItemText>
            </MenuItem>
          ))
        }
      </Menu>

      {/* Color Menu */}
      <Menu open={Boolean(colorMenu)} anchorEl={colorMenu} onClose={() => setColorMenu(null)}>
        {ROW_COLORS.map(c => (
          <MenuItem key={c.value} onClick={() => handleColorSelect(c.value)}>
            <Box sx={{
              width: 20, height: 20, borderRadius: 1,
              bgcolor: c.value || 'transparent',
              border: `1px solid ${theme.palette.divider}`, ml: 1,
            }} />
            <ListItemText>{c.label}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default TenderTable;
